import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  of,
  tap,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse, TenantInfo } from './models';

/**
 * TenantService manages the current tenant's registration status and profile data.
 *
 * Calls GET /api/portal/me to check whether the authenticated user has completed
 * registration. Caches the result after the first successful call to avoid
 * repeated network requests (used by RegistrationGuard and feature components).
 *
 * Requirements: 7.1, 7.5
 */
@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly tenantSubject = new BehaviorSubject<TenantInfo | null>(null);
  private loaded = false;

  /** Observable stream of the current tenant info (null if not registered or not yet loaded). */
  readonly tenant$: Observable<TenantInfo | null> = this.tenantSubject.asObservable();

  /** Observable that emits true when the user has a completed tenant profile. */
  readonly isRegistered$: Observable<boolean> = this.tenant$.pipe(
    map((tenant) => tenant !== null)
  );

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetch the current user's tenant info from the backend.
   * Returns cached data if already loaded successfully.
   *
   * @returns Observable emitting TenantInfo if registered, or null if not (403).
   */
  loadTenant(): Observable<TenantInfo | null> {
    if (this.loaded) {
      return of(this.tenantSubject.value);
    }

    return this.http
      .get<ApiResponse<TenantInfo>>(`${environment.apiUrl}/api/portal/me`)
      .pipe(
        map((response) => {
          if (response.ok && response.data) {
            return response.data;
          }
          return null;
        }),
        tap((tenant) => {
          this.tenantSubject.next(tenant);
          if (tenant) {
            this.loaded = true;
          }
        }),
        catchError(() => {
          // 403 or network error — user is not registered
          this.tenantSubject.next(null);
          return of(null);
        })
      );
  }

  /**
   * Force a refresh of tenant data from the backend, bypassing the cache.
   * Useful after registration completes to update the cached state.
   */
  refresh(): Observable<TenantInfo | null> {
    this.loaded = false;
    return this.loadTenant();
  }

  /**
   * Clear cached tenant data (e.g., on logout).
   */
  clear(): void {
    this.loaded = false;
    this.tenantSubject.next(null);
  }
}
